/**
 * Out-days on the Grid (#1268) — the pure shaping layer.
 *
 * `GET /api/dispatch/out-days` returns one row per (person, date). The board
 * only ever draws one day at a time, so the shell folds those rows into a
 * per-person lookup the lane headers and helper chips can read directly.
 *
 * Kept free of React so the "who is out, and what does the lane say" rule can
 * be pinned by unit test.
 */

export interface OutDayRow {
    id: string;
    userId: string;
    /** YYYY-MM-DD. */
    date: string;
    /** pto | call_in | ncns | training | other */
    reason: string;
    /** Already resolved server-side — never blank. */
    label: string;
    /** Office-entered blocks can be cleared from the board; PTO can't. */
    clearable: boolean;
}

export interface OutState {
    label: string;
    reason: string;
    clearable: boolean;
}

/**
 * Fold the window's rows into `userId -> out state` for ONE date.
 *
 * The unique index on (user_id, unavailable_date) means at most one row per
 * person per day, so first-wins is not a tiebreak — it's the only row.
 */
export function buildOutByTech(rows: OutDayRow[] | undefined, date: string): Map<string, OutState> {
    const map = new Map<string, OutState>();
    for (const r of rows ?? []) {
        if (r.date !== date) continue;
        if (map.has(r.userId)) continue;
        map.set(r.userId, { label: r.label, reason: r.reason, clearable: r.clearable });
    }
    return map;
}

/**
 * What a lane header says. The reason is already folded into the label by the
 * server, so the board reads "Out — PTO" / "Out — BPAT class" the same way for
 * every kind of absence: one shape, whatever the source.
 */
export function outBadgeText(state: OutState): string {
    return `Out — ${state.label}`;
}

/** A helper put back with their usual lead when their out-day went away. */
export interface RePaired {
    date: string;
    leadName: string;
}

/** "Thu Nov 5" — how the board talks about one day. Parsed at UTC noon so the
 *  viewer's timezone can't shunt it to the neighbouring date. */
export function formatOutDay(date: string): string {
    const d = new Date(`${date}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return date;
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}

/** The sentence a toast adds when a helper went back to their lead, or "". */
export function describeRePaired(firstName: string, rePaired: RePaired[] | undefined): string {
    const hit = (rePaired ?? []).find((r) => r.leadName);
    if (!hit) return "";
    return `${firstName} is back with ${hit.leadName.split(" ")[0]} on ${formatOutDay(hit.date)}.`;
}
